import { useCallback } from "react";
import type { QuickActionHandler } from "@/components/blockchain/types";

type UseQuickActionOptions = {
  /**
   * Function to set the chat input value
   */
  setInput: (value: string) => void;
  /**
   * Function to send a message
   */
  sendMessage: (message: {
    role: "user";
    parts: Array<{ type: "text"; text: string }>;
  }) => void;
  /**
   * Optional callback to focus the input after setting value
   */
  focusInput?: () => void;
};

/**
 * Hook to create a quick action handler for blockchain components
 *
 * @example
 * ```tsx
 * const { handleQuickAction } = useQuickAction({
 *   setInput,
 *   sendMessage,
 * });
 *
 * return <TransactionDetails onQuickAction={handleQuickAction} />;
 * ```
 */
export function useQuickAction({
  setInput,
  sendMessage,
  focusInput,
}: UseQuickActionOptions) {
  const handleQuickAction: QuickActionHandler = useCallback(
    (prompt: string, autoSend = false) => {
      if (autoSend) {
        sendMessage({
          role: "user",
          parts: [{ type: "text", text: prompt }],
        });
      } else {
        setInput(prompt);

        // Focus the input if callback provided, otherwise try to find it
        if (focusInput) {
          focusInput();
        } else {
          // Attempt to focus the chat input
          requestAnimationFrame(() => {
            const input = document.querySelector<HTMLTextAreaElement>(
              '[data-testid="chat-input"], textarea[placeholder*="message"]'
            );
            if (input) {
              input.focus();
              // Move cursor to end
              input.setSelectionRange(input.value.length, input.value.length);
            }
          });
        }
      }
    },
    [setInput, sendMessage, focusInput]
  );

  return { handleQuickAction };
}

/**
 * Utility to create contextual prompts for blockchain data
 */
export const promptGenerators = {
  /**
   * Generate a prompt to explain a transaction
   */
  explainTransaction: (txHash: string, chainName?: string) => {
    const chain = chainName ? ` on ${chainName}` : "";
    return `Explain in detail what happened in transaction ${txHash}${chain}. Break down the token transfers, contract interactions, and what the user was trying to accomplish.`;
  },

  /**
   * Generate a prompt to ask about an address
   */
  aboutAddress: (address: string, context?: string) => {
    const ctx = context ? ` This address ${context}.` : "";
    return `Tell me about wallet or contract ${address}.${ctx} What kind of activity does it have? Is it a known entity?`;
  },

  /**
   * Generate a prompt to check transaction safety
   */
  checkTransactionSafety: (txHash: string) => {
    return `Analyze transaction ${txHash} for potential risks or suspicious patterns. Could this be related to a scam, hack, or malicious activity? Should I be concerned?`;
  },

  /**
   * Generate a prompt to get more transactions
   */
  moreTransactions: (address: string, chainName: string) => {
    return `Show me more recent transactions from ${address} on ${chainName}. What has this wallet been doing lately?`;
  },

  /**
   * Generate a prompt to analyze wallet activity
   */
  analyzeWallet: (address: string) => {
    return `Create a detailed profile of wallet ${address}. What kind of user is this? Trader, holder, DeFi user? What's their activity pattern and strategy?`;
  },

  /**
   * Generate a prompt about a token
   */
  aboutToken: (tokenAddress: string, tokenSymbol?: string) => {
    const symbol = tokenSymbol ? ` (${tokenSymbol})` : "";
    return `Tell me about the token at ${tokenAddress}${symbol}. What is it? What's its purpose? Is it safe to hold or interact with?`;
  },

  /**
   * Generate a prompt to check token safety
   */
  checkTokenSafety: (tokenAddress: string, tokenSymbol?: string) => {
    const symbol = tokenSymbol ? ` (${tokenSymbol})` : "";
    return `Analyze the token at ${tokenAddress}${symbol} for potential risks. Has the contract been audited? Are there any red flags or known vulnerabilities?`;
  },

  /**
   * Generate a prompt to summarize transaction list
   */
  summarizeActivity: (address: string, chainName: string) => {
    return `Summarize the transaction activity for wallet ${address} on ${chainName}. What has this wallet been doing? Any notable patterns or significant transactions?`;
  },

  /**
   * Generate a prompt to calculate fees
   */
  calculateFees: (address: string, count: number) => {
    return `Calculate the total gas fees spent by ${address} in these ${count} transactions. Show me the breakdown and total cost.`;
  },

  /**
   * Generate a prompt to find patterns
   */
  findPatterns: (address: string) => {
    return `Analyze the transactions from ${address} and identify any patterns. Are there recurring interactions, potential MEV activity, or unusual behavior?`;
  },

  /**
   * Generate a prompt about DeFi exposure
   */
  defiExposure: (address: string, chainName: string) => {
    return `What DeFi protocols is ${address} using on ${chainName}? Show me their positions, any active loans, LP positions, and potential risks.`;
  },

  /**
   * Generate a prompt to check for NFTs
   */
  checkNfts: (address: string) => {
    return `Does ${address} hold any NFTs? Show me their collection and any notable pieces.`;
  },

  /**
   * Generate a prompt for wallet risk assessment
   */
  riskAssessment: (address: string) => {
    return `Analyze the risk profile of wallet ${address}. Has it interacted with any known scam contracts, mixers, or suspicious addresses? What's its overall risk score?`;
  },
};

export type PromptGenerator = keyof typeof promptGenerators;
